import { BookingWithDetail, EventQuestionType } from "@garther/interfaces";
import { Popup } from "@garther/ui/components";
import { useI18n } from "@garther/ui/libs";
import { MessageSquare } from "lucide-react";

interface AttendeeAnswersDrawerProps {
  booking: BookingWithDetail | null;
  onClose: () => void;
}

export function AttendeeAnswersDrawer({ booking, onClose }: AttendeeAnswersDrawerProps) {
  const { t, formatDate } = useI18n();

  const answeredQuestions =
    booking?.answers.filter((answer) => {
      if (answer.question.type === EventQuestionType.MULTIPLE_CHOICE) {
        return answer.values.length > 0;
      }
      return answer.value !== null && answer.value !== "";
    }) ?? [];

  const sessionDate = booking?.ticketTier.eventSession.startAt
    ? formatDate(booking.ticketTier.eventSession.startAt, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      })
    : null;

  return (
    <Popup
      isOpen={booking !== null}
      onClose={onClose}
      showCloseButton={true}
      closeOnBackdropClick={true}
      closeOnEscape={true}
      className="max-w-4xl"
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="pb-4 border-b border-border">
          <p className="text-responsive-lg font-medium text-text-primary">
            {t("eventDetail.attendees.answers.title")}
          </p>
          {booking && (
            <div className="mt-2 space-y-0.5">
              <p className="text-responsive-base font-medium text-text-primary">{booking.user.name}</p>
              <p className="text-responsive-sm text-text-secondary">{booking.user.email}</p>
              {sessionDate && <p className="text-responsive-sm text-text-secondary">{sessionDate}</p>}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {answeredQuestions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center space-y-4">
              <div className="bg-background-highlight p-5 rounded-full">
                <MessageSquare className="w-10 h-10 text-text-secondary" strokeWidth={1.5} />
              </div>
              <div className="space-y-1">
                <p className="text-responsive-base font-medium text-text-primary">
                  {t("eventDetail.attendees.answers.noAnswers")}
                </p>
                <p className="text-responsive-sm text-text-secondary">
                  {t("eventDetail.attendees.answers.noAnswersDescription")}
                </p>
              </div>
            </div>
          ) : (
            answeredQuestions.map((answer) => {
              let displayValue: string;

              if (answer.question.type === EventQuestionType.MULTIPLE_CHOICE) {
                displayValue = answer.values.join(", ");
              } else {
                displayValue = answer.value ?? "";
              }

              const subQMap = answer.question.subQuestions as Record<
                string,
                Array<{ label: string; required: boolean }>
              > | null;
              const subQList: Array<{ label: string; required: boolean }> =
                subQMap?.[answer.value ?? ""] ?? [];
              const subAnswers = (answer.subValue as string[] | null) ?? [];

              return (
                <div key={answer.id} className="rounded-md bg-background-highlight p-4 space-y-1.5">
                  <p className="text-responsive-sm font-medium text-text-secondary">
                    {answer.question.label}
                  </p>
                  <p className="text-responsive-base text-text-primary">{displayValue}</p>
                  {subQList.length > 0 && (
                    <div className="mt-2 pl-3 border-l-2 border-border space-y-2">
                      {subQList.map((subQ, i) => (
                        <div key={i} className="space-y-0.5">
                          <p className="text-responsive-xs font-medium text-text-secondary">{subQ.label}</p>
                          <p className="text-responsive-sm text-text-primary">{subAnswers[i] ?? "—"}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </Popup>
  );
}
